/*
 * Copyright (c) 2026 CalAmp Corp. All Rights Reserved
 */
import React, {FocusEvent, useCallback} from 'react';
import {makeStyles, TextField, Typography} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import FormItem from '@ion/dls/src/components/forms/FormItem';
import Slider from '@ion/dls/src/components/slider/Slider';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {PegFieldProps} from '../../types';

const useStyles = makeStyles(() => ({
  sliderFormItem : {
    width    : '100%',
    maxWidth : 'none'
  },
  sliderLabel : {
    color         : dlsColor('grey1'),
    fontSize      : '0.875rem',
    fontFamily    : 'Roboto',
    display       : 'inline-block',
    paddingBottom : '5px'
  },
  sliderWrapper : {
    backgroundColor : dlsColor('white'),
    padding         : '30px 30px 40px'
  },
  labelInputContainer : {
    marginBottom            : '15px',
    display                 : 'flex',
    justifyContent          : 'space-between',
    alignItems              : 'center',
    gap                     : '10px',
    '& .MuiTypography-root' : {
      paddingLeft : '0 !important',
      fontSize    : '0.875rem'
    },
    '& .MuiTextField-root' : {
      maxWidth                                    : '200px',
      '& .MuiInput-underline.Mui-disabled:before' : {
        borderBottomStyle : 'solid'
      },
      '& .MuiInput-input' : {
        paddingLeft : 0
      }
    }
  },
  inputFieldCotainer : {
    display       : 'flex',
    flexDirection : 'column',
    '& > p'       : {
      display      : 'inline-block',
      color        : dlsColor('grey3'),
      paddingRight : '0px !important'
    }
  }
}));

/**
 * Defines a reusable function that validates whether the entered value is less
 * than the minimum allowed value and returns `true` or `false` accordingly. 
 */
export const isValidMin = ({target: {value: inputValue}}, min): boolean => (
  inputValue === '' || Number(inputValue) < Number(min)
);

/**
 * Defines the `PegNumericSlider` component, which renders a slider control
 * for selecting numeric values. A continuous slider is appropriate for
 * wide numeric ranges.
 * 
 * ```typescript
 * 
 *    <PegNumericSlider
 *      min={0}
 *      max={1}
 *      value={value}
 *      onSlide={function(){}} />
 * 
 * ```
 */
const PegNumericSlider = ({label, value, onChange, min, max, unit, disabled}: PegFieldProps) => {
  const classes = useStyles();

  const handleChange = useCallback(({target: {value: inputValue}}) => {
    const parsedValue = parseFloat(inputValue);
    if (Number(inputValue) <= Number(max)) {
      onChange?.(parsedValue);
    }
  }, [onChange, min, max]);

  const handleBlur = useCallback((evt: FocusEvent<HTMLInputElement>) => {
    if (isValidMin(evt, min)) {
      onChange?.(Number(min));
    }
  }, [onChange, min]);

  return (
    <FormItem className={classes.sliderFormItem}>
      <label className={classes.sliderLabel}>{label}</label>
      <div className={classes.sliderWrapper}>
        <div className={classes.labelInputContainer}>
          <Typography>
            {unit?.toLocaleUpperCase()}
          </Typography>
          <div className={classes.inputFieldCotainer}>
            <TextField type='number'
              value={Number(value)}
              onChange={handleChange}
              onBlur={handleBlur}
              inputProps={{min, max}}
              disabled={disabled}
              size='small' />
            <Typography>{translate('peg-wizard:stepsCard.wakeSleep.validRange', {min, max})}</Typography>
          </div>
        </div>
        <Slider value={Number(value)}
          min={min}
          max={max}
          input={true}
          disabled={disabled}
          onSlide={function(_, val) {
            onChange?.(Number(val));
          }} />
      </div>
    </FormItem>
  );
};

export {PegNumericSlider};
